import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { getWardenPending, wardenAction, getWardenActivePasses, getWardenClosedPasses, markStudentReturned } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Clock, CheckCircle2, Eye, Loader2, Phone, AlertCircle, Printer, UserCheck, Calendar, Search } from "lucide-react";
import TodayPastPasses from "../components/TodayPastPasses";

const WardenDashboard = () => {
  return (
    <DashboardLayout title="Hostel Warden Portal">
      <Routes>
        <Route index element={<WardenHome />} />
      </Routes>
    </DashboardLayout>
  );
};

const WardenHome = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  
  const [wardenRemarks, setWardenRemarks] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data } = await getWardenPending();
      setPending(data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPass(null);
    setActionType(null);
    setRejectionReason("");
    setWardenRemarks("");
  };

  const handleAction = async () => {
    if (actionType === "reject" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    setSubmitting(true);
    try {
      const { data: updatedPass } = await wardenAction(selectedPass.id, {
        action: actionType,
        parent_call: null,
        rejection_reason: actionType === "reject" ? rejectionReason : null,
        remarks: wardenRemarks || null
      });
      toast.success(actionType === "approve" ? "Gate pass approved! Opening print..." : "Request rejected");
      const approvedPassId = selectedPass.id;
      resetForm();
      loadData();
      // Auto-open print in new tab after warden approval
      if (actionType === "approve") {
        window.open(`/print/${approvedPassId}`, "_blank");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="warden-dashboard">
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="pending" data-testid="warden-pending-tab" className="text-xs sm:text-sm">
            <Clock className="h-4 w-4 mr-1" /> Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="return" data-testid="warden-return-tab" className="text-xs sm:text-sm">
            <UserCheck className="h-4 w-4 mr-1" /> Return Entry
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Final Approval</p>
                <p className="text-2xl font-semibold text-amber-600">{pending.length}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Requests Pending Final Approval</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : pending.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-300" />
              <p>No pending requests!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request No.</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Parent Contact</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Exit Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((req) => (
                  <TableRow key={req.id} data-testid={`pending-row-${req.id}`}>
                    <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.student_name}</p>
                        <p className="text-xs text-slate-500">{req.course_name} - Y{req.year}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{req.parent_name}</p>
                        <p className="text-xs text-blue-600 font-mono">{req.parent_phone}</p>
                      </div>
                    </TableCell>
                    <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                    <TableCell>{formatDate(req.exit_date)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedPass(req); setActionType(null); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => { setSelectedPass(req); setActionType("approve"); }}
                          data-testid={`approve-btn-${req.id}`}
                        >
                          <Phone className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={() => { setSelectedPass(req); setActionType("reject"); }}
                          data-testid={`reject-btn-${req.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={!!selectedPass && actionType === "approve"} onOpenChange={resetForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Approve Gate Pass</DialogTitle>
          </DialogHeader>
          
          {selectedPass && (
            <div className="space-y-6">
              {/* Request Summary */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Request:</span>
                  <span className="font-mono">{selectedPass.request_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Student:</span>
                  <span>{selectedPass.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Exit:</span>
                  <span>{formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Destination:</span>
                  <span>{selectedPass.destination}</span>
                </div>
                <div className="pt-2 border-t mt-2">
                  <p className="text-blue-600 font-medium">Parent Contact: {selectedPass.parent_name} - {selectedPass.parent_phone}</p>
                </div>
              </div>

              {/* Warden Remarks */}
              <div className="space-y-2">
                <Label>Warden Remarks (Optional)</Label>
                <Textarea 
                  placeholder="Any additional remarks"
                  value={wardenRemarks}
                  onChange={(e) => setWardenRemarks(e.target.value)}
                  rows={2}
                  data-testid="warden-remarks-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button 
              onClick={handleAction}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-approval-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve Gate Pass"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={!!selectedPass && actionType === "reject"} onOpenChange={resetForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject Gate Pass</DialogTitle>
          </DialogHeader>
          
          {selectedPass && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <p><span className="text-slate-500">Request:</span> {selectedPass.request_number}</p>
                <p><span className="text-slate-500">Student:</span> {selectedPass.student_name}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-red-600">Rejection Reason *</Label>
                <Textarea
                  placeholder="Enter the reason for rejection (required)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="border-red-200"
                  data-testid="rejection-reason-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button 
              onClick={handleAction}
              disabled={submitting}
              variant="destructive"
              data-testid="confirm-rejection-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!selectedPass && !actionType} onOpenChange={() => setSelectedPass(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gate Pass Details</DialogTitle>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-500">Request Number</p>
                  <p className="font-mono">{selectedPass.request_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <StatusBadge status={selectedPass.status} />
                </div>
                <div>
                  <p className="text-slate-500">Student</p>
                  <p className="font-medium">{selectedPass.student_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Course / Year</p>
                  <p>{selectedPass.course_name} - Year {selectedPass.year}</p>
                </div>
                <div>
                  <p className="text-slate-500">Parent Name</p>
                  <p>{selectedPass.parent_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Parent Phone</p>
                  <p className="font-mono text-blue-600">{selectedPass.parent_phone}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Reason</p>
                  <p>{selectedPass.reason}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Destination</p>
                  <p>{selectedPass.destination}</p>
                </div>
              </div>
              
              {/* Approval History */}
              {selectedPass.approval_history?.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="font-medium mb-2">Approval History</p>
                  {selectedPass.approval_history.map((h, i) => (
                    <div key={i} className="text-sm p-3 bg-emerald-50 rounded mb-2 flex justify-between items-start">
                      <div>
                        <p className="font-medium">{h.approver_name}</p>
                        <p className="text-xs text-slate-500 capitalize">{h.stage?.replace(/_/g, " ")} - {h.action}</p>
                        {h.remarks && <p className="text-xs text-slate-600 mt-1">Remarks: {h.remarks}</p>}
                      </div>
                      <p className="text-xs text-slate-400 whitespace-nowrap ml-4">
                        {h.timestamp ? new Date(h.timestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

        </TabsContent>

        <TabsContent value="return">
          <ReturnEntryTab />
        </TabsContent>
      </Tabs>

      {/* Gate Pass Activity - at bottom */}
      <TodayPastPasses showPrint={true} />
    </div>
  );
};

const ReturnEntryTab = () => {
  const [activePasses, setActivePasses] = useState([]);
  const [closedPasses, setClosedPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returnDialog, setReturnDialog] = useState(null);
  const [returnData, setReturnData] = useState({ return_date: "", return_time: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const [closedDate, setClosedDate] = useState("");
  const [closedLoading, setClosedLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadActive(); }, []);

  const loadActive = async () => {
    try {
      const { data } = await getWardenActivePasses();
      setActivePasses(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const loadClosed = async () => {
    if (!closedDate) { toast.error("Select a date"); return; }
    setClosedLoading(true);
    try {
      const { data } = await getWardenClosedPasses(closedDate);
      setClosedPasses(data);
    } catch { toast.error("Failed to load"); }
    finally { setClosedLoading(false); }
  };

  const handleReturn = async () => {
    if (!returnData.return_date || !returnData.return_time) {
      toast.error("Return date and time are required");
      return;
    }
    setSubmitting(true);
    try {
      await markStudentReturned(returnDialog.id, returnData);
      toast.success(`${returnDialog.student_name} marked as returned. Pass closed.`);
      setReturnDialog(null);
      setReturnData({ return_date: "", return_time: "", remarks: "" });
      loadActive();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark return");
    } finally {
      setSubmitting(false);
    }
  };

  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];
  const nowTime = now.toTimeString().slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Active Passes - Students Currently Out */}
      <Card className="border-amber-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-amber-600" />
            Students Currently Out ({activePasses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : activePasses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-emerald-300" />
              <p>No students currently out. All passes closed.</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50 text-xs">
                    <TableHead className="text-xs">Req No.</TableHead>
                    <TableHead className="text-xs">Student</TableHead>
                    <TableHead className="text-xs">Course/Yr</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Exit Date/Time</TableHead>
                    <TableHead className="text-xs">Expected Return</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activePasses.map((p) => (
                    <TableRow key={p.id} data-testid={`active-pass-${p.id}`}>
                      <TableCell className="font-mono text-xs">{p.request_number}</TableCell>
                      <TableCell className="font-medium text-sm">{p.student_name}</TableCell>
                      <TableCell className="text-xs">{p.course_name}-Y{p.year}</TableCell>
                      <TableCell><PassTypeBadge type={p.pass_type} /></TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{formatDate(p.exit_date)} {p.exit_time}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{p.expected_return_date ? `${formatDate(p.expected_return_date)} ${p.expected_return_time || ""}` : "-"}</TableCell>
                      <TableCell><StatusBadge status={p.status} /></TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7"
                          onClick={() => { setReturnDialog(p); setReturnData({ return_date: todayStr, return_time: nowTime, remarks: "" }); }}
                          data-testid={`mark-return-${p.id}`}
                        >
                          <UserCheck className="h-3 w-3 mr-1" /> Return
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Closed Passes History */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Closed Passes (Returned Students)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 mb-4">
            <div className="space-y-1">
              <Label className="text-sm">Filter by Return Date</Label>
              <Input
                type="date"
                value={closedDate}
                onChange={(e) => setClosedDate(e.target.value)}
                className="w-48"
                data-testid="closed-date-input"
              />
            </div>
            <Button size="sm" onClick={loadClosed} disabled={closedLoading} data-testid="load-closed-btn">
              {closedLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
              View
            </Button>
          </div>
          {closedPasses.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-xs">
                    <TableHead className="text-xs">Req No.</TableHead>
                    <TableHead className="text-xs">Student</TableHead>
                    <TableHead className="text-xs">Course/Yr</TableHead>
                    <TableHead className="text-xs">Exit</TableHead>
                    <TableHead className="text-xs">Return Date/Time</TableHead>
                    <TableHead className="text-xs">Remarks</TableHead>
                    <TableHead className="text-xs">Recorded By</TableHead>
                    <TableHead className="text-xs">Print</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {closedPasses.map((p) => (
                    <TableRow key={p.id} data-testid={`closed-pass-${p.id}`}>
                      <TableCell className="font-mono text-xs">{p.request_number}</TableCell>
                      <TableCell className="text-sm font-medium">{p.student_name}</TableCell>
                      <TableCell className="text-xs">{p.course_name}-Y{p.year}</TableCell>
                      <TableCell className="text-xs">{formatDate(p.exit_date)} {p.exit_time}</TableCell>
                      <TableCell className="text-xs font-medium text-emerald-600">
                        {p.return_entry ? `${formatDate(p.return_entry.return_date)} ${p.return_entry.return_time}` : "-"}
                      </TableCell>
                      <TableCell className="text-xs max-w-[150px] truncate">{p.return_entry?.remarks || "-"}</TableCell>
                      <TableCell className="text-xs">{p.return_entry?.recorded_by || "-"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7" onClick={() => navigate(`/print/${p.id}`)}>
                          <Printer className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center py-6 text-slate-400 text-sm">{closedDate ? "No closed passes for this date." : "Select a date to view closed passes."}</p>
          )}
        </CardContent>
      </Card>

      {/* Return Entry Dialog */}
      <Dialog open={!!returnDialog} onOpenChange={() => setReturnDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Student Return</DialogTitle>
          </DialogHeader>
          {returnDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-semibold">{returnDialog.student_name}</p>
                <p className="text-slate-500">{returnDialog.course_name} - Year {returnDialog.year}</p>
                <p className="text-xs text-slate-400 mt-1">Pass: {returnDialog.request_number} | Exit: {formatDate(returnDialog.exit_date)} {returnDialog.exit_time}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Return Date *</Label>
                  <Input
                    type="date"
                    value={returnData.return_date}
                    onChange={(e) => setReturnData({ ...returnData, return_date: e.target.value })}
                    data-testid="return-date-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Return Time *</Label>
                  <Input
                    type="time"
                    value={returnData.return_time}
                    onChange={(e) => setReturnData({ ...returnData, return_time: e.target.value })}
                    data-testid="return-time-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Remarks</Label>
                <Textarea
                  value={returnData.remarks}
                  onChange={(e) => setReturnData({ ...returnData, remarks: e.target.value })}
                  placeholder="Any remarks about student's return"
                  rows={2}
                  data-testid="return-remarks-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleReturn} disabled={submitting} data-testid="confirm-return-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserCheck className="h-4 w-4 mr-1" />}
                  Confirm Return & Close Pass
                </Button>
                <Button variant="outline" onClick={() => setReturnDialog(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WardenDashboard;
